/**
 * Script pour promouvoir le premier utilisateur comme super admin
 * À exécuter une seule fois lors de la configuration initiale
 */

import { supabase } from '../lib/supabase';

interface PromoteResponse {
  success: boolean; 
  message: string;
}

/**
 * Promeut un utilisateur au rang de super administrateur
 * @param {string} userEmail - Email de l'utilisateur à promouvoir
 * @returns {Promise<PromoteResponse>}
 */
export async function promoteFirstSuperAdmin(userEmail: string): Promise<PromoteResponse> {
  console.log('[promoteFirstAdmin] Tentative de promotion pour:', userEmail);
  
  try {
    // 1. Vérifier si des administrateurs existent déjà
    console.log('[promoteFirstAdmin] Vérification des administrateurs existants');
    const { count, error: countError } = await supabase
      .from('admin_users')
      .select('*', { count: 'exact', head: true });
    
    console.log('[promoteFirstAdmin] Résultat du comptage:', { count, error: countError });
    
    if (countError) {
      console.error('[promoteFirstAdmin] Erreur lors de la vérification des administrateurs existants:', countError);
      return { success: false, message: 'Erreur lors de la vérification des administrateurs existants.' };
    }
    
    if (count && count > 0) {
      console.log('[promoteFirstAdmin] Des administrateurs existent déjà:', count);
      return { success: false, message: 'Des administrateurs existent déjà dans le système.' };
    }
    
    // 2. Trouver l'utilisateur par son email
    console.log('[promoteFirstAdmin] Recherche de l\'utilisateur par email:', userEmail);
    const { data: userData, error: userError } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', userEmail)
      .single();
    
    console.log('[promoteFirstAdmin] Résultat de la recherche:', { userData, error: userError });
    
    if (userError || !userData) {
      console.error('[promoteFirstAdmin] Erreur lors de la recherche de l\'utilisateur:', userError);
      return { success: false, message: 'Utilisateur non trouvé.' };
    }
    
    // 3. Récupérer l'ID du rôle super_admin
    console.log('[promoteFirstAdmin] Récupération du rôle super_admin');
    const { data: roleData, error: roleError } = await supabase
      .from('admin_roles')
      .select('id')
      .eq('name', 'super_admin')
      .single();
    
    console.log('[promoteFirstAdmin] Résultat de la recherche du rôle:', { roleData, error: roleError });
    
    if (roleError || !roleData) {
      console.error('[promoteFirstAdmin] Erreur lors de la récupération du rôle super_admin:', roleError);
      return { success: false, message: 'Rôle super_admin non trouvé.' };
    }
    
    // 4. Ajouter l'utilisateur comme super admin
    console.log('[promoteFirstAdmin] Ajout de l\'utilisateur comme super admin:', {
      user_id: userData.id,
      role_id: roleData.id
    });
    
    const { error: insertError } = await supabase
      .from('admin_users')
      .insert({
        user_id: userData.id,
        role_id: roleData.id
      });
    
    console.log('[promoteFirstAdmin] Résultat de l\'insertion:', { error: insertError });
    
    if (insertError) {
      console.error('[promoteFirstAdmin] Erreur lors de la promotion de l\'utilisateur:', insertError);
      return { success: false, message: 'Erreur lors de la promotion de l\'utilisateur.' };
    }
    
    // 5. Mettre à jour le statut d'admin dans la table profiles
    console.log('[promoteFirstAdmin] Mise à jour du statut admin dans profiles');
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ is_admin: true })
      .eq('id', userData.id);
    
    console.log('[promoteFirstAdmin] Résultat de la mise à jour:', { error: updateError });
    
    if (updateError) {
      console.error('[promoteFirstAdmin] Erreur lors de la mise à jour du profil:', updateError);
      return { success: false, message: 'Erreur lors de la mise à jour du profil.' };
    }
    
    console.log('[promoteFirstAdmin] Promotion réussie pour:', userEmail);
    
    return { 
      success: true, 
      message: `L'utilisateur ${userEmail} a été promu au rang de super administrateur avec succès.` 
    };
  } catch (error) {
    console.error('[promoteFirstAdmin] Erreur inattendue:', error);
    return { success: false, message: 'Une erreur inattendue s\'est produite.' };
  }
} 